import { useMutation, UseMutationResult } from 'react-query';
import { api } from '../../api';

import queryClient from '../../query-client';
import { useFirmContext } from '../../providers/FirmProvider';
import { GetPaymentMethodsResponse } from './use-payment-methods';

type DefaultPaymentMethodRequest = {
    paymentMethodId: string;
};

async function setDefaultPaymentMethod({ paymentMethodId }: DefaultPaymentMethodRequest, firmId: string): Promise<void> {
    await api.post(`/firms/${firmId}/subscription/v2/payment-methods/default`, {
        paymentMethodId,
    });
}

export function useSetDefaultPaymentMethod(): UseMutationResult<void, unknown, DefaultPaymentMethodRequest, unknown> {
    const { id } = useFirmContext();
    return useMutation((req) => setDefaultPaymentMethod(req, id), {
        onMutate: async ({ paymentMethodId }) => {
            await queryClient.cancelQueries({ queryKey: ['payment-methods'] });
            const previous = queryClient.getQueryData(['payment-methods']);

            queryClient.setQueryData<GetPaymentMethodsResponse>(['payment-methods'], (old) => {
                if (!old) {
                    return { paymentMethods: [] };
                }
                return {
                    paymentMethods: old.paymentMethods.map((pm) => {
                        if (pm.id === paymentMethodId) {
                            return {
                                ...pm,
                                isDefault: true,
                            };
                        }
                        return {
                            ...pm,
                            isDefault: false,
                        };
                    }),
                };
            });

            return previous;
        },
        onSettled: () => {
            queryClient.invalidateQueries(['payment-methods']);
        },
        onError: (_, __, context) => {
            queryClient.setQueryData(['payment-methods'], context);
        },
    });
}
